/**
 * Local Storage Management Utilities for Chat History
 * Implements displacement strategy and scroll-to-reload functionality
 */

const STORAGE_KEYS = {
  CHAT_HISTORY: 'rmdv_chat_history',
  CHAT_SESSIONS: 'rmdv_chat_sessions',
  CHAT_SETTINGS: 'rmdv_chat_settings'
};

const STORAGE_LIMITS = {
  MAX_MESSAGES: 1000, // Maximum messages to store
  MAX_STORAGE_SIZE: 50 * 1024 * 1024, // 50MB limit
  MESSAGES_PER_PAGE: 50, // Messages to load per scroll
  COMPRESSION_THRESHOLD: 100 // Compress messages longer than this
};

/**
 * Persist messages losslessly.
 *
 * The previous implementation truncated every message to 100 chars and could
 * not restore it — so once history was actually loaded (see issue B2) users
 * would see mangled past messages. Overall storage is bounded by MAX_MESSAGES
 * and MAX_STORAGE_SIZE plus the oldest-message trimming in the save path, so
 * there is no need to mutilate individual message content here.
 */
const compressMessage = (message) => message;

/**
 * Pass-through: content is stored in full, so nothing to restore.
 */
const decompressMessage = (message) => message;

/**
 * Check if localStorage is available
 */
const isStorageAvailable = () => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get storage quota information
 */
export const getStorageQuota = () => {
  if (!isStorageAvailable()) {
    return { available: false, used: 0, quota: 0 };
  }

  try {
    // Estimate used space (rough calculation)
    let used = 0;
    for (const key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        used += localStorage[key].length + key.length;
      }
    }

    return {
      available: true,
      used,
      quota: STORAGE_LIMITS.MAX_STORAGE_SIZE,
      percentage: (used / STORAGE_LIMITS.MAX_STORAGE_SIZE) * 100
    };
  } catch {
    return { available: false, used: 0, quota: 0 };
  }
};

/**
 * Check if storage quota is exceeded
 */
export const isStorageQuotaExceeded = () => {
  const quota = getStorageQuota();
  return !quota.available || quota.percentage > 90; // 90% threshold
};

/**
 * Save chat message to localStorage with displacement
 */
export const saveChatMessage = (message, sessionId = 'default') => {
  if (!isStorageAvailable()) return false;

  try {
    const key = `${STORAGE_KEYS.CHAT_HISTORY}_${sessionId}`;
    // Load the FULL history (capped at MAX_MESSAGES, same pattern as
    // exportChatHistory below) — not the default paginated page=1/pageSize=50
    // slice. Using the default page here previously discarded every message
    // beyond the most recent 50 on every save, silently truncating history
    // long before the intended >MAX_MESSAGES displacement policy ever ran.
    let messages = loadChatHistory(sessionId, 1, STORAGE_LIMITS.MAX_MESSAGES);

    // Add new message
    messages.push({
      ...message,
      savedAt: new Date().toISOString(),
      sessionId
    });

    // Apply displacement if needed
    if (messages.length > STORAGE_LIMITS.MAX_MESSAGES) {
      // Keep most recent messages, remove older ones
      const keepCount = Math.floor(STORAGE_LIMITS.MAX_MESSAGES * 0.8); // Keep 80%
      messages = messages.slice(-keepCount);
    }

    // Compress long messages
    messages = messages.map(compressMessage);

    // Check storage quota before saving
    if (isStorageQuotaExceeded()) {
      // Compress more aggressively
      messages = messages.map(msg => ({
        ...msg,
        content: msg.content.substring(0, 50) + '...[compressed]'
      }));
    }

    localStorage.setItem(key, JSON.stringify(messages));
    return true;
  } catch (error) {
    console.warn('Failed to save chat message:', error);
    return false;
  }
};

/**
 * Load chat history from localStorage
 */
export const loadChatHistory = (sessionId = 'default', page = 1, pageSize = STORAGE_LIMITS.MESSAGES_PER_PAGE) => {
  if (!isStorageAvailable()) return [];

  try {
    const key = `${STORAGE_KEYS.CHAT_HISTORY}_${sessionId}`;
    const data = localStorage.getItem(key);

    if (!data) return [];

    let messages = JSON.parse(data);

    // Decompress messages
    messages = messages.map(decompressMessage);

    // Sort by timestamp
    messages.sort((a, b) => new Date(a.timestamp || a.savedAt) - new Date(b.timestamp || b.savedAt));

    // Implement pagination for scroll-to-reload
    const startIndex = Math.max(0, messages.length - (page * pageSize));
    const endIndex = messages.length - ((page - 1) * pageSize);

    return messages.slice(startIndex, endIndex);
  } catch (error) {
    console.warn('Failed to load chat history:', error);
    return [];
  }
};

/**
 * Get paginated chat history for scroll-to-reload
 */
export const getChatHistoryPage = (sessionId = 'default', page = 1) => {
  const messages = loadChatHistory(sessionId, page);
  const totalMessages = getTotalMessageCount(sessionId);
  const hasMore = (page * STORAGE_LIMITS.MESSAGES_PER_PAGE) < totalMessages;

  return {
    messages,
    hasMore,
    page,
    totalMessages
  };
};

/**
 * Get total message count for a session
 */
export const getTotalMessageCount = (sessionId = 'default') => {
  if (!isStorageAvailable()) return 0;

  try {
    const key = `${STORAGE_KEYS.CHAT_HISTORY}_${sessionId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data).length : 0;
  } catch {
    return 0;
  }
};

/**
 * Clear chat history for a session
 */
export const clearChatHistory = (sessionId = 'default') => {
  if (!isStorageAvailable()) return false;

  try {
    const key = `${STORAGE_KEYS.CHAT_HISTORY}_${sessionId}`;
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Save chat session metadata
 */
export const saveChatSession = (sessionId, metadata) => {
  if (!isStorageAvailable()) return false;

  try {
    const sessions = loadChatSessions();
    sessions[sessionId] = {
      ...metadata,
      lastUpdated: new Date().toISOString(),
      messageCount: getTotalMessageCount(sessionId)
    };

    localStorage.setItem(STORAGE_KEYS.CHAT_SESSIONS, JSON.stringify(sessions));
    return true;
  } catch {
    return false;
  }
};

/**
 * Load chat sessions metadata
 */
export const loadChatSessions = () => {
  if (!isStorageAvailable()) return {};

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CHAT_SESSIONS);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

/**
 * Save chat settings
 */
export const saveChatSettings = (settings) => {
  if (!isStorageAvailable()) return false;

  try {
    localStorage.setItem(STORAGE_KEYS.CHAT_SETTINGS, JSON.stringify({
      ...settings,
      savedAt: new Date().toISOString()
    }));
    return true;
  } catch {
    return false;
  }
};

/**
 * Load chat settings
 */
export const loadChatSettings = () => {
  if (!isStorageAvailable()) return {};

  try {
    const data = localStorage.getItem(STORAGE_KEYS.CHAT_SETTINGS);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

/**
 * Cleanup old data to free up space
 */
export const cleanupOldData = (daysOld = 30) => {
  if (!isStorageAvailable()) return 0;

  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    let cleanedCount = 0;
    const sessions = loadChatSessions();

    // Remove old sessions
    for (const [sessionId, metadata] of Object.entries(sessions)) {
      if (new Date(metadata.lastUpdated) < cutoffDate) {
        clearChatHistory(sessionId);
        delete sessions[sessionId];
        cleanedCount++;
      }
    }

    localStorage.setItem(STORAGE_KEYS.CHAT_SESSIONS, JSON.stringify(sessions));
    return cleanedCount;
  } catch {
    return 0;
  }
};

/**
 * Export chat history for backup
 */
export const exportChatHistory = (sessionId = 'default') => {
  const messages = loadChatHistory(sessionId, 1, STORAGE_LIMITS.MAX_MESSAGES);
  return {
    sessionId,
    exportedAt: new Date().toISOString(),
    messages,
    metadata: {
      totalMessages: messages.length,
      storageQuota: getStorageQuota()
    }
  };
};

/**
 * Import chat history from backup
 */
export const importChatHistory = (exportData, sessionId = null) => {
  if (!isStorageAvailable() || !exportData.messages) return false;

  try {
    const targetSessionId = sessionId || exportData.sessionId || 'imported';
    const key = `${STORAGE_KEYS.CHAT_HISTORY}_${targetSessionId}`;

    localStorage.setItem(key, JSON.stringify(exportData.messages));

    // Update session metadata
    saveChatSession(targetSessionId, {
      importedAt: new Date().toISOString(),
      originalSessionId: exportData.sessionId
    });

    return true;
  } catch {
    return false;
  }
};